import { useEffect, useMemo, useState } from 'react';
import { getUserFromToken } from '../../../context/authContext';
import {
  Shield,
  CalendarBlank,
  ListBullets,
  Plus,
} from '@phosphor-icons/react';
import {
  GuardsCalendar,
  GuardsTable,
  NewPlanningModal,
  TechniciansAvatarGroup,
} from '../components';
import { teamAssignmentService } from '../services/teamAssignmentService.js';
import { teamService } from '../../../services/teams/teamService.js';
import { getCalendarWeeks, startOfDay } from '../utils/weeklyCalendar.js';
import PageHeader from '../../../shared/components/ui/PageHeader.jsx';

function toDateOnly(value) {
  if (!value) return null;
  const part = String(value).slice(0, 10);
  const [y, m, d] = part.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function overlaps(startA, endA, startB, endB) {
  const aEnd = endA || new Date(8640000000000000);
  const bEnd = endB || new Date(8640000000000000);
  return startA <= bEnd && startB <= aEnd;
}

export default function GuardiasPage() {
  const now = new Date();
  const [viewMode, setViewMode] = useState('calendar');
  const [showNewPlanningModal, setShowNewPlanningModal] = useState(false);
  const [teams, setTeams] = useState([]);
  const [teamAssignments, setTeamAssignments] = useState([]);
  const [onDutyRecords, setOnDutyRecords] = useState([]);
  const [calendarOnDutyRecords, setCalendarOnDutyRecords] = useState([]);
  const [summaryOnDutyRecords, setSummaryOnDutyRecords] = useState([]);
  const [hasSummarySnapshot, setHasSummarySnapshot] = useState(false);
  const [hasLoadedOnDuty, setHasLoadedOnDuty] = useState(false);
  const [summaryReference] = useState(() => {
    const today = new Date();
    return {
      month: today.getMonth(),
      year: today.getFullYear(),
    };
  });
  const [calendarMonth, setCalendarMonth] = useState(now.getMonth());
  const [calendarYear, setCalendarYear] = useState(now.getFullYear());
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableCurrentPage, setTableCurrentPage] = useState(1);
  const [tableTotalPages, setTableTotalPages] = useState(1);
  const [tableTotalItems, setTableTotalItems] = useState(0);
  const [tableFilters, setTableFilters] = useState({ status: '' });
  const [openListAtLastPage, setOpenListAtLastPage] = useState(false);
  const [isBootstrapped, setIsBootstrapped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState('');
  const canManage = getUserFromToken()?.role === 'admin';

  const teamsById = useMemo(() => {
    return teams.reduce((acc, team) => {
      acc[team.id] = team;
      return acc;
    }, {});
  }, [teams]);

  const normalizeOnDuty = (records) => {
    return (Array.isArray(records) ? records : [])
      .map((record) => {
        const teamIdRaw = record.teamId || record.team?.id || record.team?._id || record.team?.teamId;
        const teamId = teamIdRaw != null ? String(teamIdRaw) : '';
        const startAt = String(record.startAt || '').slice(0, 10);
        const endAt = record.endAt ? String(record.endAt).slice(0, 10) : null;
        if (!teamId || !startAt) return null;
        const hasInterruptReason = Boolean(record.reason || record.interruptReason || record.interruptionReason);
        const isInterrupted = record.isInterrupted != null
          ? Boolean(record.isInterrupted)
          : hasInterruptReason;
        return {
          id: `${teamId}-${startAt}`,
          teamId,
          startAt,
          endAt,
          isSupport: Boolean(record.isSupport),
          interruptReason: record.reason || record.interruptReason || record.interruptionReason || '',
          supportTeamId: record.supportTeamId ? String(record.supportTeamId) : '',
          isInterrupted,
          startDate: toDateOnly(startAt),
          endDate: endAt ? toDateOnly(endAt) : null,
        };
      })
      .filter(Boolean);
  };

  const normalizeTeamAssignments = (records) => {
    return (Array.isArray(records) ? records : [])
      .map((assignment) => {
        const user = assignment.user || {};
        const teamIdRaw = assignment.teamId || assignment.team?.id || assignment.team?._id;
        const teamId = teamIdRaw != null ? String(teamIdRaw) : '';
        if (!teamId) return null;

        return {
          teamId,
          isActive: Boolean(assignment.isActive),
          technician: {
            name: user.name,
            email: user.email,
            phone: user.phone
          },
        };
      })
      .filter((assignment) => assignment && assignment.isActive);
  };

  const fetchTeams = async () => {
    const result = await teamService.getTeams({ hasGeoData: false });

    const mapped = (Array.isArray(result) ? result : [])
      .map((team) => ({
        id: String(team.id || team._id || team.teamId || ''),
        name: team.name || `Equipo ${team.id || ''}`,
        description: team.description || '',
        isActive: team.isActive !== false,
      }))
      .filter((team) => team.id && team.isActive);

    setTeams(mapped);
  };

  const fetchTeamAssignments = async () => {
    const result = await teamAssignmentService.getAssignments();
    setTeamAssignments(normalizeTeamAssignments(result));
  };

  const extractOnDutyRecords = (result) => {
    const rawRecords = Array.isArray(result)
      ? result
      : (result?.responses || result?.records || result?.data || result?.onDuty || []);

    return normalizeOnDuty(rawRecords);
  };

  const fetchOnDuty = async ({
    view = viewMode,
    page = tableCurrentPage,
    pageSize = tablePageSize,
    month = calendarMonth,
    year = calendarYear,
    filters = tableFilters,
  } = {}) => {
    const isListView = view === 'list' || view === 'table';
    const params = new URLSearchParams();
    if (isListView) {
      params.append('pageSize', String(pageSize));
      params.append('pageCurrent', String(Math.max(0, page - 1)));
      // Agregar filtros como parámetros de query
      if (filters.status) {params.append('status', filters.status);}
    } else {
      params.append('month', String(month + 1));
      params.append('year', String(year));
    }

    const query = {
      ...(isListView
        ? {
            pageSize,
            pageCurrent: Math.max(0, page - 1),
            ...(filters.status ? { status: filters.status } : {})
          }
        : {
            month: month + 1,
            year
          })
    };
    const result = await teamService.getOnDuty(query);

    const normalizedRecords = extractOnDutyRecords(result);

    if (isListView) {
      const backendTotalItems = Number(result?.pagination?.totalItems);
      const resolvedTotalItems = Number.isFinite(backendTotalItems) ? backendTotalItems : normalizedRecords.length;
      const resolvedTotalPages = Math.max(1, Math.ceil(resolvedTotalItems / pageSize));

      setTableTotalItems(resolvedTotalItems);
      setTableTotalPages(resolvedTotalPages);

      if (Array.isArray(result)) {
        const start = (page - 1) * pageSize;
        const end = start + pageSize;
        setOnDutyRecords(normalizedRecords.slice(start, end));
      } else {
        setOnDutyRecords(normalizedRecords);
      }
      return { records: normalizedRecords, totalPages: resolvedTotalPages };
    }

    setOnDutyRecords(normalizedRecords);
    setCalendarOnDutyRecords(normalizedRecords);
    return { records: normalizedRecords, totalPages: null };
  };

  const reloadCalendarSnapshot = async () => {
    const result = await teamService.getOnDuty({
      month: calendarMonth + 1,
      year: calendarYear,
    });

    setCalendarOnDutyRecords(extractOnDutyRecords(result));
  };

  const reloadSummarySnapshot = async () => {
    const result = await teamService.getOnDuty({
      month: summaryReference.month + 1,
      year: summaryReference.year,
    });

    setSummaryOnDutyRecords(extractOnDutyRecords(result));
    setHasSummarySnapshot(true);
  };

  const refreshData = async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([fetchTeams(), fetchTeamAssignments()]);
      setIsBootstrapped(true);
    } catch (err) {
      setError(err.message || 'Error cargando datos de guardias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isBootstrapped) return;

    const loadOnDuty = async () => {
      setLoading(true);
      setError('');
      try {
        const shouldHydrateSummaryFromVisibleMonth = !hasSummarySnapshot
          && viewMode === 'calendar'
          && calendarMonth === summaryReference.month
          && calendarYear === summaryReference.year;

        const onDutyMeta = await fetchOnDuty({
          view: viewMode,
          page: tableCurrentPage,
          pageSize: tablePageSize,
          month: calendarMonth,
          year: calendarYear,
          filters: tableFilters,
        });

        if (shouldHydrateSummaryFromVisibleMonth) {
          setSummaryOnDutyRecords(onDutyMeta?.records || []);
          setHasSummarySnapshot(true);
        } else if (!hasSummarySnapshot) {
          await reloadSummarySnapshot();
        }

        if (viewMode === 'list' && openListAtLastPage) {
          const lastPage = Math.max(1, onDutyMeta?.totalPages || 1);
          if (tableCurrentPage !== lastPage) {
            setTableCurrentPage(lastPage);
          }
          setOpenListAtLastPage(false);
        }

        setHasLoadedOnDuty(true);
      } catch (err) {
        setError(err.message || 'Error cargando datos de guardias');
      } finally {
        setLoading(false);
      }
    };

    loadOnDuty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBootstrapped, viewMode, tableCurrentPage, tablePageSize, calendarMonth, calendarYear, tableFilters]);

  const reloadOnDutyForCurrentView = async () => {
    await fetchOnDuty({
      view: viewMode,
      page: tableCurrentPage,
      pageSize: tablePageSize,
      month: calendarMonth,
      year: calendarYear,
      filters: tableFilters,
    });
  };

  const createOnDuty = async (teamId, startAt, endAt) => {
    await teamService.createOnDuty({ teamId, startAt, endAt });
  };

  const deleteOnDuty = async (teamId, startAt) => {
    await teamService.deleteOnDuty({ teamId, startAt });
  };
  const interruptOnDuty = async (teamId, startAt, endAt, reason, supportTeamId) => {
    await teamService.interruptOnDuty({ teamId, startAt, endAt, reason, supportTeamId });
  };
  const restoreOnDuty = async (teamId, startAt, endAt) => {
    await teamService.restoreOnDuty({ teamId, startAt, endAt });
  };


  const handleAssignWeek = async ({ teamId, startAt, endAt, existingRecord, existingRecords }) => {
    if (!teamId || !startAt || !endAt) throw new Error('Datos incompletos para asignar guardia');

    const startDate = toDateOnly(startAt);
    const endDate = toDateOnly(endAt);
    if (!startDate || !endDate || endDate < startDate) {
      throw new Error('Rango de fechas invalido para asignar guardia');
    }

    const previousRecord = existingRecord || existingRecords?.[0] || null;

    setIsMutating(true);
    setError('');
    try {
      if (previousRecord && previousRecord.teamId !== teamId) {
        await deleteOnDuty(previousRecord.teamId, previousRecord.startAt);
      }

      if (!previousRecord || previousRecord.teamId !== teamId) {
        await createOnDuty(teamId, startAt, endAt);
      }

      await Promise.all([reloadOnDutyForCurrentView(), reloadSummarySnapshot(), reloadCalendarSnapshot()]);
    } catch (err) {
      throw err;
    } finally {
      setIsMutating(false);
    }
  };

  const handleRemoveWeek = async ({ teamId, startAt }) => {
    if (!teamId || !startAt) throw new Error('Datos incompletos para quitar guardia');

    setIsMutating(true);
    setError('');
    try {
      await deleteOnDuty(teamId, startAt);
      await Promise.all([reloadOnDutyForCurrentView(), reloadSummarySnapshot(), reloadCalendarSnapshot()]);
    } catch (err) {
      setError(err.message || 'Error quitando guardia');
      throw err;
    } finally {
      setIsMutating(false);
    }
  };

  const handleInterruptGuard = async ({ currentRecord, reason, supportTeamId, startAt, endAt }) => {
    if (!currentRecord?.teamId || !currentRecord?.startAt) {
      throw new Error('No se encontro la guardia a interrumpir');
    }
    if (!supportTeamId || !startAt || !endAt) {
      throw new Error('Datos incompletos para interrumpir la guardia');
    }

    setIsMutating(true);
    setError('');
    try {
      await interruptOnDuty(currentRecord.teamId, startAt, endAt, reason, supportTeamId);
      await Promise.all([reloadOnDutyForCurrentView(), reloadSummarySnapshot(), reloadCalendarSnapshot()]);
    } catch (err) {
      throw err;
    } finally {
      setIsMutating(false);
    }
  };

  const handleRestoreGuard = async ({ currentRecord, endAt }) => {
    if (!currentRecord?.teamId || !currentRecord?.startAt) {
      throw new Error('No se encontro la guardia a restaurar');
    }
    if (!endAt) {
      throw new Error('Falta la fecha de fin para restaurar la guardia');
    }

    setIsMutating(true);
    setError('');
    try {
      const restoreStartDate = toDateOnly(currentRecord.startAt);
      const restoreEndDate = toDateOnly(endAt);

      const overlappingRecords = onDutyRecords.filter((record) => {
        if (!record?.teamId || !record?.startDate) return false;
        if (record.teamId === currentRecord.teamId && record.startAt === currentRecord.startAt) return false;
        if (!record.isSupport) return false;

        return overlaps(restoreStartDate, restoreEndDate, record.startDate, record.endDate);
      });

      for (const record of overlappingRecords) {
        await deleteOnDuty(record.teamId, record.startAt);
      }
      await restoreOnDuty(currentRecord.teamId, currentRecord.startAt, endAt);
      await Promise.all([reloadOnDutyForCurrentView(), reloadSummarySnapshot(), reloadCalendarSnapshot()]);
    } catch (err) {
      setError(err.message || 'Error restaurando guardia');
      throw err;
    } finally {
      setIsMutating(false);
    }
  };

  const handleBulkPlanningSave = async (assignments) => {
    if (!Array.isArray(assignments) || assignments.length === 0) {
      throw new Error('No hay semanas para guardar');
    }

    setIsMutating(true);
    setError('');
    try {
      for (const item of assignments) {
        await createOnDuty(item.teamId, item.startAt, item.endAt);
      }
      await Promise.all([reloadOnDutyForCurrentView(), reloadSummarySnapshot(), reloadCalendarSnapshot()]);
    } catch (err) {
      throw err;
    } finally {
      setIsMutating(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const summaryRecords = hasSummarySnapshot ? summaryOnDutyRecords : onDutyRecords;

  const currentGuard = useMemo(() => {
    const candidates = summaryRecords.filter((record) => {
      if (!record.startDate) return false;
      const end = record.endDate || new Date(8640000000000000);
      return today >= record.startDate && today <= end;
    });
    return candidates.sort((a, b) => b.startDate - a.startDate)[0] || null;
  }, [summaryRecords, today]);

  const nextGuard = useMemo(() => {
    const candidates = summaryRecords
      .filter((record) => record.startDate && record.startDate > today)
      .sort((a, b) => a.startDate - b.startDate);
    return candidates[0] || null;
  }, [summaryRecords, today]);

  const techniciansByTeamId = useMemo(() => {
    return teamAssignments.reduce((acc, assignment) => {
      const list = acc[assignment.teamId] || [];
      const identity = assignment.technician.email || assignment.technician.name;
      const alreadyExists = list.some((tech) => (tech.email || tech.name) === identity);
      if (!alreadyExists) {
        list.push(assignment.technician);
      }
      acc[assignment.teamId] = list;
      return acc;
    }, {});
  }, [teamAssignments]);

  const currentGuardTechnicians = currentGuard ? (techniciansByTeamId[currentGuard.teamId] || []) : [];
  const nextGuardTechnicians = nextGuard ? (techniciansByTeamId[nextGuard.teamId] || []) : [];

  const plannedWeeksInMonth = useMemo(() => {
    const firstDay = new Date(calendarYear, calendarMonth, 1);
    const weeks = getCalendarWeeks(calendarYear, calendarMonth);
    const coveredWeeks = weeks.reduce((count, week) => {
      const hasRecord = calendarOnDutyRecords.some((record) => {
        if (!record.startDate) return false;
        const startDate = startOfDay(record.startDate);
        return startDate >= week.weekStart && startDate <= week.weekEnd;
      });

      return count + (hasRecord ? 1 : 0);
    }, 0);

    return {
      coveredWeeks,
      totalWeeks: weeks.length,
      month: firstDay.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })
    };
  }, [calendarOnDutyRecords, calendarMonth, calendarYear]);

  return (
    <div className="bg-bg overflow-y-auto text-content">
      <PageHeader
        title="Gestión de Guardias"
        subtitle="Planificación semanal de equipos de guardia"
        icon={<Shield size={28} />}
        actions={(
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center bg-surface-2 border border-line rounded-xl p-1">
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'calendar'
                    ? 'bg-amber text-on-amber'
                    : 'text-content-muted hover:text-content'
                }`}
              >
                <CalendarBlank size={16} />
                Calendario
              </button>
              <button
                onClick={() => {
                  setTableCurrentPage(1);
                  setOpenListAtLastPage(true);
                  setViewMode('list');
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'list'
                    ? 'bg-amber text-on-amber'
                    : 'text-content-muted hover:text-content'
                }`}
              >
                <ListBullets size={16} />
                Lista
              </button>
            </div>

            {canManage && (
              <button
                onClick={() => setShowNewPlanningModal(true)}
                className="bg-amber hover:bg-amber-hover text-on-amber px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2 font-semibold text-sm"
              >
                <Plus size={16} weight="bold" />
                Nueva Planificacion
              </button>
            )}
          </div>
        )}
      />

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-6 items-stretch">
        <div className="aspect-square md:aspect-auto bg-surface border border-line rounded-xl px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-content text-xs">Equipo de Guardia</p>
            <p className="text-lg font-bold text-success mt-0.5">{currentGuard ? (teamsById[currentGuard.teamId]?.name || 'Equipo') : '-'}</p>
            <div className="mt-1">
              {currentGuard
                ? <TechniciansAvatarGroup technicians={currentGuardTechnicians} maxVisible={4} showCount />
                : <span className="text-xs text-content-muted">-</span>}
            </div>
            <p className="text-xs text-content mt-1">Semana actual</p>
          </div>

        </div>

        <div className="aspect-square md:aspect-auto bg-surface border border-line rounded-xl px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-content text-xs">Próximo Equipo</p>
            <p className="text-lg font-bold text-amber mt-0.5">{nextGuard ? (teamsById[nextGuard.teamId]?.name || 'Equipo') : '-'}</p>
            <div className="mt-1">
              {nextGuard
                ? <TechniciansAvatarGroup technicians={nextGuardTechnicians} maxVisible={4} showCount />
                : <span className="text-xs text-content-muted">-</span>}
            </div>
            <p className="text-xs text-content mt-1">{nextGuard ? nextGuard.startAt : 'Sin próximas guardias'}</p>
          </div>

        </div>

        <div className="aspect-square md:aspect-auto bg-surface border border-line rounded-xl px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-content text-xs">Semanas Planificadas</p>
            <p className="text-lg font-bold text-content mt-0.5">{plannedWeeksInMonth.coveredWeeks} / {plannedWeeksInMonth.totalWeeks}</p>
            <p className="text-xs text-content mt-1">{plannedWeeksInMonth.month}</p>
          </div>

        </div>
        <div className="aspect-square md:aspect-auto bg-surface border border-line rounded-xl px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-content text-xs">Horario de Guardia</p>
            <p className="text-lg font-bold text-content mt-0.5">Lunes 9:00</p>
            <p className="text-xs text-content mt-1">Inicio y fin semanal</p>
          </div>

        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-error bg-error/12 p-3 text-sm text-error">
          {error}
        </div>
      )}

      {loading && !hasLoadedOnDuty && viewMode !== 'calendar' && (
        <div className="mb-6 rounded-lg border border-line bg-surface-2 p-4 text-content-muted">
          Cargando guardias y equipos...
        </div>
      )}

      {(viewMode === 'calendar' ? (
        <GuardsCalendar
          teams={teams}
          onDutyRecords={onDutyRecords}
          initialMonth={calendarMonth}
          initialYear={calendarYear}
          onMonthChange={(month, year) => {
            setCalendarMonth(month);
            setCalendarYear(year);
          }}
          techniciansByTeamId={techniciansByTeamId}
          onAssignWeek={handleAssignWeek}
          onRemoveWeek={handleRemoveWeek}
          onInterruptGuard={handleInterruptGuard}
          onRestoreGuard={handleRestoreGuard}
          onDeleteGuard={handleRemoveWeek}
          isMutating={isMutating}
          isLoading={loading}
          canManage={canManage}
        />
      ) : hasLoadedOnDuty ? (
        <GuardsTable
          records={onDutyRecords}
          teamsById={teamsById}
          techniciansByTeamId={techniciansByTeamId}
          currentPage={tableCurrentPage}
          totalPages={tableTotalPages}
          pageSize={tablePageSize}
          totalItems={tableTotalItems}
          onPageChange={setTableCurrentPage}
          onPageSizeChange={(nextSize) => {
            setTableCurrentPage(1);
            setTablePageSize(nextSize);
          }}
          filters={tableFilters}
          onFilterChange={setTableFilters}
          onRestoreGuard={handleRestoreGuard}
          onDeleteGuard={handleRemoveWeek}
          isLoading={loading}
          isMutating={isMutating}
          canManage={canManage}
        />
      ) : null)}

      <NewPlanningModal
        open={showNewPlanningModal}
        onOpenChange={setShowNewPlanningModal}
        teams={teams}
        isSaving={isMutating}
        onSave={handleBulkPlanningSave}
      />
    </div>
  );
}
